import Box from "@material-ui/core/Box";
import moment from "moment";
import React, { FC } from "react";
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  Record,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TextField,
} from "react-admin";
import bookings from ".";
import CrudBreadcrumb from "../../components/CrudBreadcrumb";
import StatusField from "../../components/StatusField";
import { BookingIdField, HospitalRoomField } from "../../components/fields";
import ProductInstanceField from "../../components/fields/ProductInstanceIdField";
import { Booking } from "../../types";
import { foundIn } from "../../utils";
import { IssueBulkActions, IssueStatus } from "../issues";
import { ShowActions } from "./BookingActions";
import BookingInvoice from "./BookingInvoices";
import BookingIssue from "./BookingIssue";
import BookingItems from "./BookingItems";
import BookingRevision from "./BookingRevision";
import BookingTopUp from "./BookingTopUp";
import CancellationInfo from "./CancellationInfo";
import Delivery from "./Deliveries";
import OutboundTransactionNumbers from "./OutboundTransactionNumber";
import ReportItems from "./ReportItems";
import Allocate from "./allocations/Allocate";

export const BookingShow: FC<ShowProps> = (props) => {
  // const dataProvider = useDataProvider();

  // const fetchBookingTopUp = async () => {
  //   try {
  //     if (!props.id) {
  //       console.error("Missing id in props");
  //       return;
  //     }
  //     const response = await dataProvider.getOne("bookings/", {
  //       id: props.id,
  //     });
  //     console.log(response.data);
  //   } catch (error) {
  //     console.error("Error fetching extra data:", error);
  //   }
  // };

  // useEffect(() => {
  //   fetchBookingTopUp();
  // }, [props.id]);

  return (
    <>
      <CrudBreadcrumb
        resourceName={bookings.name}
        mode="show"
        resourceId={props.id}
      />
      <Show {...props} actions={<ShowActions />}>
        <TabbedShowLayout>
          <Tab label="summary">
            <BookingIdField />
            <TextField source="consultant.full_name" label="Consultant" />
            <TextField source="business_unit.name" label="Business Unit" />

            <FunctionField
              label="Booking Created At"
              render={(record: any) =>
                record.created_at
                  ? moment(record.created_at)
                      .tz("Asia/Singapore")
                      .format("DD MMMM yyyy - hh:mm A")
                  : ""
              }
            />

            <FunctionField
              label="Surgery Dates"
              render={(record: any) => {
                // Handle both old single date format and new dates array format
                if (
                  record.dates &&
                  Array.isArray(record.dates) &&
                  record.dates.length > 0
                ) {
                  return (
                    <div>
                      {record.dates.map((dateObj: any, index: number) => {
                        // Extract date from object (API returns array of objects with date property)
                        const dateValue =
                          typeof dateObj === "string" ? dateObj : dateObj?.date;

                        // Validate date before formatting
                        if (!dateValue) return null;

                        const momentDate = moment(dateValue);
                        if (!momentDate.isValid()) {
                          return (
                            <div key={index} style={{ marginBottom: 4 }}>
                              <strong>Case {index + 1}:</strong> Invalid date
                            </div>
                          );
                        }

                        return (
                          <div key={index} style={{ marginBottom: 4 }}>
                            <strong>Case {index + 1}:</strong>{" "}
                            {momentDate.format("DD MMMM yyyy - hh:mm A")}
                          </div>
                        );
                      })}
                    </div>
                  );
                } else if (record.date) {
                  // Fallback to old single date format
                  const momentDate = moment(record.date);
                  if (!momentDate.isValid()) {
                    return "Invalid date";
                  }
                  return momentDate.format("DD MMMM yyyy - hh:mm A");
                }
                return "";
              }}
            />
            <TextField source="surgeon.full_name" label="Surgeon" />
            <HospitalRoomField
              source="hospital_room.name"
              hospitalSource="hospital.name"
            />
            <TextField
              source="procedure.name"
              label={
                process.env.REACT_APP_COMPANY_CODE === "STMY"
                  ? "Order Type"
                  : "Procedure"
              }
            />

            <TextField source="additional_items" />

            <TextField source="remarks" />

            {process.env.REACT_APP_COMPANY_CODE === "STMY" && (
              <OutboundTransactionNumbers />
            )}

            <BookingInvoice />

            <BookingTopUp />

            {/* Cancellation Information */}
            <CancellationInfo />

            <BookingRevision />
          </Tab>
          <Tab label="Booking Issue">
            <BookingIssue {...props} />
          </Tab>
          <Tab label="Booking Items">
            <BookingItems {...props} />
          </Tab>
          <Tab label="Issues History">
            <Box>
              <List
                basePath="/issues"
                resource="issues"
                exporter={false}
                actions={<></>}
                bulkActionButtons={<IssueBulkActions />}
                sort={{ field: "created_at", order: "desc" }}
                filter={{ booking_id: props.id }}
              >
                <Datagrid
                  // rowClick="show"
                  isRowSelectable={(record: Record) =>
                    foundIn(record.status, [IssueStatus.OPEN])
                  }
                >
                  <TextField source="id" label="Issue ID" />
                  <DateField source="created_at" showTime={true} />
                  <ProductInstanceField source="product_instance" />
                  <TextField source="description" />
                  <StatusField source="status" />
                </Datagrid>
              </List>
            </Box>
          </Tab>
          <Tab label="Allocations">
            <Allocations />
          </Tab>
          <Tab label="Deliveries">
            <Delivery />
          </Tab>
          {/* We don't need chat tabs for now */}
          {/* <Tab label="Chat">
            <Chat />
          </Tab> */}
          <Tab label="Reports">
            <ReportItems {...props} />
          </Tab>
        </TabbedShowLayout>
      </Show>
    </>
  );
};

interface TabContentProps<T> {
  record?: T;
}

/**
 * Allocation window
 *
 * @param props
 * @returns
 */
const Allocations: React.FC<TabContentProps<Booking>> = (props) => {
  const { record: booking } = props;

  if (!booking) {
    return <Box>Loading Booking...</Box>;
  }

  return <Allocate bookingId={booking.id} />;
};
